import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { resetUserPassword } from '../api.admin';
import { adminPasswordResetText, fillTemplate } from '../i18n/pages/adminPasswordReset';
import { useLang } from '../i18n/LanguageContext';

const MIN_LENGTH = 8;
const MAX_BYTES = 72;

const getToken = () => localStorage.getItem('token') || '';

// Everything a keyboard user can land on. Disabled controls are excluded on purpose, so the list is
// recomputed on every Tab: while a request is pending the buttons and inputs are all disabled.
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PasswordResetTarget {
  id: number;
  firstName?: string;
  lastName?: string;
  email: string;
}

export const displayName = (u: PasswordResetTarget) =>
  `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;

type Props = {
  target: PasswordResetTarget;
  onClose: () => void;
  /** Called once, after the server confirmed. Receives the user's display name, never the password. */
  onSuccess: (name: string) => void;
};

/**
 * Admin sets another user's password (issue #124). The password lives only in this component's state:
 * it is never written to storage, the URL or the console, and both fields are cleared on close.
 */
const AdminPasswordResetModal: React.FC<Props> = ({ target, onClose, onSuccess }) => {
  const { lang } = useLang();
  const t = adminPasswordResetText[lang];
  const titleId = useId();
  const firstInput = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  // Captured during the first render, i.e. before this dialog moves focus: the control that opened it.
  const [opener] = useState<Element | null>(() => document.activeElement);
  const inFlight = useRef(false);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const bytes = new TextEncoder().encode(password).length;
  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const tooLong = bytes > MAX_BYTES;
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= MIN_LENGTH && !tooLong && confirm === password;

  const close = () => {
    if (inFlight.current) return; // an in-flight request must finish so its outcome is shown
    setPassword('');
    setConfirm('');
    onClose();
  };

  useEffect(() => {
    firstInput.current?.focus();
  }, []);

  // Modal means the page behind it is out of reach: not focusable, not clickable, not announced.
  // The dialog is portalled to <body>, outside the app root, so it is not affected. On close the
  // attributes go back to what they were, and focus returns to the control that opened the dialog.
  useEffect(() => {
    const appRoot = document.getElementById('root');
    const hadInert = appRoot?.hasAttribute('inert') ?? false;
    const prevAriaHidden = appRoot?.getAttribute('aria-hidden') ?? null;
    appRoot?.setAttribute('inert', '');
    appRoot?.setAttribute('aria-hidden', 'true');
    return () => {
      if (appRoot) {
        if (!hadInert) appRoot.removeAttribute('inert');
        if (prevAriaHidden === null) appRoot.removeAttribute('aria-hidden');
        else appRoot.setAttribute('aria-hidden', prevAriaHidden);
      }
      // After the root is reachable again; skip an opener that has since left the page.
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, [opener]);

  // The focused button disables itself when the request starts, which would drop focus to <body>;
  // keep it in the dialog, and hand it back to the form if the request fails and the dialog stays.
  const wasPending = useRef(false);
  useEffect(() => {
    const dialog = dialogRef.current;
    const active = document.activeElement;
    if (pending && dialog && (dialog.contains(active) || !active || active === document.body)) {
      dialog.focus();
    } else if (!pending && wasPending.current) {
      firstInput.current?.focus();
    }
    wasPending.current = pending;
  }, [pending]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
      const active = document.activeElement;
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!dialog.contains(active) || active === dialog) {
        // Focus is on <body> or on the dialog itself: pull it onto the nearest end.
        if (!dialog.contains(active) || e.shiftKey) {
          e.preventDefault();
          (e.shiftKey ? last : first).focus();
        }
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const messageFor = (err: any): string => {
    const code = err?.response?.data?.code;
    const status = err?.response?.status;
    switch (code) {
      case 'WEAK_PASSWORD': return t.errorWeak;
      case 'SELF_RESET_NOT_ALLOWED': return t.errorSelf;
      case 'ADMIN_RESET_DISABLED': return t.errorAdminDisabled;
      case 'USER_ANONYMIZED': return t.errorAnonymized;
      case 'USER_NOT_FOUND': return t.errorNotFound;
      default:
        if (status === 404) return t.errorNotFound;
        if (status === 403) return t.errorForbidden;
        return t.errorGeneric;
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid || inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    setServerError(null);
    try {
      await resetUserPassword(target.id, password, getToken());
      inFlight.current = false;
      setPending(false);
      setPassword('');
      setConfirm('');
      onSuccess(displayName(target));
      onClose();
    } catch (err: any) {
      // Stay open with the typed values so the admin can fix and retry. Never claim success.
      inFlight.current = false;
      setPending(false);
      setServerError(messageFor(err));
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '9px 10px', fontSize: '0.95rem',
    border: '1px solid #cbd5e0', borderRadius: 8,
  };
  const errorStyle: React.CSSProperties = { color: '#c53030', fontSize: '0.8rem', margin: '4px 0 0' };
  const canSubmit = valid && !pending;

  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ outline: 'none', background: '#fff', borderRadius: 12, padding: 24, maxWidth: 440, width: '100%', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
      >
        <h2 id={titleId} style={{ fontSize: '1.05rem', fontWeight: 700, margin: '0 0 8px', overflowWrap: 'anywhere' }}>
          {fillTemplate(t.title, { name: displayName(target), email: target.email })}
        </h2>
        <p style={{ color: '#4a5568', fontSize: '0.85rem', margin: '0 0 14px' }}>{t.intro}</p>

        <form onSubmit={submit} noValidate>
          <label htmlFor={`${titleId}-new`} style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>
            {t.newPasswordLabel}
          </label>
          <input
            id={`${titleId}-new`}
            ref={firstInput}
            type="password"
            autoComplete="new-password"
            value={password}
            disabled={pending}
            onChange={(e) => { setPassword(e.target.value); setServerError(null); }}
            style={inputStyle}
          />
          <p style={{ color: '#718096', fontSize: '0.76rem', margin: '4px 0 0' }}>{t.hint}</p>
          {tooShort && <p role="alert" style={errorStyle}>{t.errorTooShort}</p>}
          {tooLong && <p role="alert" style={errorStyle}>{t.errorTooLong}</p>}

          <label htmlFor={`${titleId}-confirm`} style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', margin: '12px 0 4px' }}>
            {t.confirmPasswordLabel}
          </label>
          <input
            id={`${titleId}-confirm`}
            type="password"
            autoComplete="new-password"
            value={confirm}
            disabled={pending}
            onChange={(e) => { setConfirm(e.target.value); setServerError(null); }}
            style={inputStyle}
          />
          {mismatch && <p role="alert" style={errorStyle}>{t.errorMismatch}</p>}

          {serverError && (
            <div role="alert" style={{ marginTop: 12, padding: '7px 12px', background: '#fff5f5', color: '#c53030', borderRadius: 6, fontSize: '0.85rem' }}>
              {serverError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 18 }}>
            <button
              type="button"
              onClick={close}
              disabled={pending}
              style={{ padding: '8px 16px', fontSize: '0.88rem', fontWeight: 600, borderRadius: 8, border: '1px solid #cbd5e0', background: '#fff', color: '#4a5568', cursor: pending ? 'not-allowed' : 'pointer' }}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              style={{ padding: '8px 16px', fontSize: '0.88rem', fontWeight: 600, borderRadius: 8, border: 'none', background: '#c05621', color: '#fff', cursor: canSubmit ? 'pointer' : 'not-allowed', opacity: canSubmit ? 1 : 0.5 }}
            >
              {pending ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default AdminPasswordResetModal;
